import React from 'react';

/**
 * StatusBadge Component
 * 
 * Displays status with consistent styling.
 * Used in PageHeader and throughout the app.
 * 
 * @example
 * ```tsx
 * <StatusBadge status="success" label="Active" />
 * <StatusBadge status="warning" label="Pending" />
 * <StatusBadge status="error" label="Failed" />
 * ```
 */

export interface StatusBadgeProps {
  /** Status type */
  status: 'success' | 'warning' | 'error' | 'info' | 'default';
  /** Badge text */
  label: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show dot indicator */
  showDot?: boolean;
  /** Additional className */
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  size = 'md',
  showDot = true,
  className = '',
}) => {
  // Status styles
  const statusStyles = {
    success: [
      'bg-success-100 text-success-700',
      'dark:bg-success-700/30 dark:text-success-50',
      'border-success-200 dark:border-success-600/30',
    ].join(' '),
    
    warning: [
      'bg-warning-100 text-warning-700',
      'dark:bg-warning-700/30 dark:text-warning-50',
      'border-warning-200 dark:border-warning-600/30',
    ].join(' '),
    
    error: [
      'bg-error-100 text-error-700',
      'dark:bg-error-700/30 dark:text-error-50',
      'border-error-200 dark:border-error-600/30',
    ].join(' '),
    
    info: [
      'bg-info-100 text-info-700',
      'dark:bg-info-700/30 dark:text-info-50',
      'border-info-200 dark:border-info-600/30',
    ].join(' '),
    
    default: [
      'bg-gray-100 text-gray-700',
      'dark:bg-gray-700/30 dark:text-gray-50',
      'border-gray-200 dark:border-gray-600/30',
    ].join(' '),
  };

  // Dot color
  const dotColors = {
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    error: 'bg-error-500',
    info: 'bg-info-500',
    default: 'bg-gray-500',
  };

  // Size styles
  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-3 py-1 text-sm gap-1.5',
    lg: 'px-4 py-1.5 text-base gap-2',
  };

  // Dot size
  const dotSize = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  };

  return (
    <span
      className={`
        inline-flex items-center
        rounded-full border
        font-semibold
        ${statusStyles[status]}
        ${sizeStyles[size]}
        ${className}
      `}
    >
      {showDot && (
        <span className={`rounded-full ${dotSize[size]} ${dotColors[status]}`} />
      )}
      {label}
    </span>
  );
};

StatusBadge.displayName = 'StatusBadge';

export default StatusBadge;
